"use client";

import { useState, useEffect } from "react";

import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  MenuItem,
  Paper,
  Select,
  InputLabel,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";

import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import { istDatetimeLocalToISOString, toISTDatetimeLocal } from "../../helper";
import ChipToken, { getChipColor } from "../../components/ChipToken";

const nowStr = () => toISTDatetimeLocal(new Date());

export default function CreateRakeDrawer({ open, onClose, refresh }) {
  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;

  const [loading, setLoading]     = useState(false);
  const [tables, setTables]       = useState([]);
  const [error, setError]         = useState("");
  const [onTableChips, setOnTableChips] = useState({});

  const [form, setForm] = useState({
    table_name: "",
    date:       nowStr(),
    chips_in:   [],
    status:     "OPEN",
    remarks:    "",
  });

  useEffect(() => {
    if (!open) return;

    setForm((f) => ({ ...f, date: nowStr(), table_name: "", remarks: "" }));
    setError("");

    // Fetch tables
    axiosRequest("GET", `${apiBaseUrl}/tables`, token)
      .then((res) => setTables(res?.data?.data || []))
      .catch(() => setTables([]));

    // Fetch chip denominations and on-table chips
    Promise.all([
      axiosRequest("GET", `${apiBaseUrl}/chips`, token),
      axiosRequest("GET", `${apiBaseUrl}/shift-management/current-summary`, token),
    ]).then(([chipsRes, summaryRes]) => {
        const chips = Array.isArray(chipsRes?.data) ? chipsRes.data : (chipsRes?.data?.data ?? []);
        const rows  = chips
          .sort((a, b) => a.denomination - b.denomination)
          .map((c) => ({ denomination: c.denomination, quantity: "" }));
        setForm((f) => ({ ...f, chips_in: rows }));
        setOnTableChips(summaryRes?.data?.on_table_chips || {});
      })
      .catch(() => {});
  }, [open]);

  const calcAmount = (denom, qty) => Number(denom || 0) * Number(qty || 0);

  const totalIn = form.chips_in.reduce(
    (sum, c) => sum + calcAmount(c.denomination, c.quantity),
    0
  );

  const handleQtyChange = (index, value) => {
    const updated = [...form.chips_in];
    updated[index] = { ...updated[index], quantity: value };
    setForm({ ...form, chips_in: updated });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError("");
      const payload = {
        ...form,
        date:        form.date ? istDatetimeLocalToISOString(form.date) : new Date().toISOString(),
        total_in:   totalIn,
        total_tips: 0,
        rake_amount: totalIn,
      };
      const res = await axiosRequest("POST", `${apiBaseUrl}/rake`, token, payload);
      if (res?.status === "fail" || res?.status === false) {
        setError(res?.message || "Failed to create rake.");
        return;
      }
      refresh();
      onClose();
    } catch (err) {
      console.log(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 650, p: 3 }}>

        {/* HEADER */}
        <Box mb={3}>
          <Typography variant="h5" fontWeight={700}>Create Rake</Typography>
          <Typography color="text.secondary">Manage table chips</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>

          {/* TABLE NAME */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Table Name</InputLabel>
              <Select
                label="Table Name"
                value={form.table_name}
                onChange={(e) => setForm({ ...form, table_name: e.target.value })}
              >
                {tables.map((t) => (
                  <MenuItem key={t._id} value={t.tableName}>{t.tableName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* DATE & TIME */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small" type="datetime-local" label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </Grid>

          {/* CHIPS IN */}
          <Grid item xs={12}>
            <Typography fontWeight={700} mb={1}>Chips In</Typography>
          </Grid>

          {form.chips_in.map((chip, index) => {
            const cc       = getChipColor(chip.denomination);
            const onTable  = Number(onTableChips[chip.denomination] || 0);
            const isOver   = Number(chip.quantity) > onTable;
            return (
            <Grid container spacing={2} key={index} mb={1} px={1} alignItems="center">
              <Grid item xs={3}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%", px: 1.5, py: 1, borderRadius: 2, bgcolor: cc.bg, color: cc.text }}>
                  <ChipToken denomination={chip.denomination} size={36} />
                  <Typography fontWeight={700} fontSize="0.95rem">{chip.denomination}</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth size="small" label="On Table"
                  value={onTable}
                  InputProps={{ readOnly: true }}
                  sx={{ "& .MuiInputBase-input": { color: onTable === 0 ? "error.main" : "text.secondary", fontWeight: 600 } }}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth size="small" type="number" label="Quantity"
                  value={chip.quantity}
                  onChange={(e) => handleQtyChange(index, e.target.value)}
                  inputProps={{ min: 0, max: onTable }}
                  disabled={onTable === 0}
                  error={isOver}
                  helperText={isOver ? `Max ${onTable}` : ""}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth size="small" label="Amount ()"
                  value={calcAmount(chip.denomination, chip.quantity) > 0
                    ? `${calcAmount(chip.denomination, chip.quantity).toLocaleString()}`
                    : ""}
                  InputProps={{ readOnly: true }}
                  sx={{ "& .MuiInputBase-input": { color: "success.main", fontWeight: 600 } }}
                />
              </Grid>
            </Grid>
          );})}

          {/* SUMMARY */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2, mt: 1, background: totalIn > 0 ? "linear-gradient(135deg,#11998e,#38ef7d)" : "#f5f5f5" }}>
              <Typography fontWeight={700} color={totalIn > 0 ? "#fff" : "text.primary"}>
                Total Chips In: {totalIn.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>

          {/* REMARKS */}
          <Grid item xs={12}>
            <TextField
              fullWidth multiline rows={3} size="small" label="Remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            />
          </Grid>

          {/* ERROR */}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
            </Grid>
          )}

          {/* ACTIONS */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || form.chips_in.some(c => Number(c.quantity) > Number(onTableChips[c.denomination] || 0))}
              >
                {loading ? "Saving..." : "Create Rake"}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Box>
    </Drawer>
  );
}
